import { useState } from 'react';
import { MessageCircle, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { cn } from '@/lib/utils';

interface SocialListeningModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStart: (prompt: string) => void;
}

type Step = 'intro' | 'questions';

type FieldConfig = {
  id: keyof SocialListeningForm;
  label: string;
  placeholder: string;
  multiline?: boolean;
};

type SocialListeningForm = {
  brandName: string;
  brandWebsite: string;
  productCategory: string;
  statementOfIdentity: string;
  tagLine: string;
  keyProductLines: string;
  products: string;
  productMostExcitedAbout: string;
  productMostConcernedAbout: string;
  brandHqRegion: string;
  launchYear: string;
  pricePointPerUnit: string;
  primaryDistributionChannels: string;
  keyCustomers: string;
};

const initialForm: SocialListeningForm = {
  brandName: '',
  brandWebsite: '',
  productCategory: '',
  statementOfIdentity: '',
  tagLine: '',
  keyProductLines: '',
  products: '',
  productMostExcitedAbout: '',
  productMostConcernedAbout: '',
  brandHqRegion: '',
  launchYear: '',
  pricePointPerUnit: '',
  primaryDistributionChannels: '',
  keyCustomers: '',
};

const fields: FieldConfig[] = [
  {
    id: 'brandName',
    label: 'Brand Name',
    placeholder: 'e.g. Juni Organic Tea',
  },
  {
    id: 'brandWebsite',
    label: 'Brand Website',
    placeholder: 'e.g. www.drinkjuni.com',
  },
  {
    id: 'productCategory',
    label: 'Product Category',
    placeholder: 'e.g. Organic RTD Tea, Functional Water',
    multiline: true,
  },
  {
    id: 'statementOfIdentity',
    label: 'Statement of Identity (SOI)',
    placeholder: 'e.g. Sparkling Tea with Adaptogens',
  },
  {
    id: 'tagLine',
    label: 'Tag Line',
    placeholder: 'e.g. for a happy mind',
  },
  {
    id: 'keyProductLines',
    label: 'Key Product Lines',
    placeholder: 'e.g. Juni Adaptogen Sparkling Tea with Caffeine',
    multiline: true,
  },
  {
    id: 'products',
    label: 'Products',
    placeholder: 'e.g. Lemonade Iced Tea, Peach, Raspberry',
    multiline: true,
  },
  {
    id: 'productMostExcitedAbout',
    label: 'Product Most Excited About',
    placeholder: 'e.g. Lemonade Iced Tea',
  },
  {
    id: 'productMostConcernedAbout',
    label: 'Product Most Concerned About',
    placeholder: 'e.g. Lemon Zest, Decaffeinated line',
  },
  {
    id: 'brandHqRegion',
    label: 'Brand HQ / Region',
    placeholder: 'e.g. USA (National)',
  },
  {
    id: 'launchYear',
    label: 'Launch Year',
    placeholder: 'e.g. 2023',
  },
  {
    id: 'pricePointPerUnit',
    label: 'Price Point (per unit)',
    placeholder: 'e.g. $2.99 - $3.49',
  },
  {
    id: 'primaryDistributionChannels',
    label: 'Primary Distribution Channel(s)',
    placeholder: 'e.g. Natural, DTC, Grocery, Mass & Club',
    multiline: true,
  },
  {
    id: 'keyCustomers',
    label: 'Key Customers',
    placeholder: 'e.g. Whole Foods Market, Amazon, Sprouts',
    multiline: true,
  },
];

function buildPrompt(form: SocialListeningForm): string {
  const lines = fields
    .map((field) => {
      const value = form[field.id].trim();
      return value ? `${field.label}: ${value}` : '';
    })
    .filter(Boolean);

  return lines.join('\n');
}

export default function SocialListeningModal({
  isOpen,
  onClose,
  onStart,
}: SocialListeningModalProps) {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('intro');
  const [form, setForm] = useState<SocialListeningForm>(initialForm);

  const handleClose = () => {
    setStep('intro');
    setForm(initialForm);
    onClose();
  };

  const handleStart = () => {
    const currentPath = window.location.pathname;

    if (currentPath === '/main/gurus') {
      navigate('/main', { replace: true });
    }

    onStart(buildPrompt(form));
    handleClose();
  };

  const handleFieldChange = (
    fieldId: keyof SocialListeningForm,
    value: string,
  ) => {
    if (fieldId === 'launchYear' && !/^\d*$/.test(value)) {
      return;
    }

    setForm((current) => ({
      ...current,
      [fieldId]: value,
    }));
  };

  if (!isOpen) return null;

  const inputCls =
    'text-sm border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500';

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-2'>
      <div className='relative w-full max-w-3xl max-h-[90vh] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col'>
        <div className='flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-800'>
          <div className='flex items-center gap-3'>
            <div className='p-2 rounded-lg bg-blue-600'>
              <MessageCircle size={18} className='text-white' />
            </div>
            <div>
              <h2 className='text-lg font-bold text-gray-900 dark:text-white'>
                Social Listening Tool
              </h2>
              <p className='text-xs text-gray-500 dark:text-gray-400 mt-0.5'>
                Capture brand inputs for social listening and consumer analysis
              </p>
            </div>
          </div>
          <button
            type='button'
            onClick={handleClose}
            className='p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition'
          >
            <X className='size-5 text-gray-500 dark:text-gray-400' />
          </button>
        </div>

        <div className='flex items-center gap-2 px-5 pt-4 pb-1'>
          {(['intro', 'questions'] as Step[]).map((item, index) => {
            const labels = ['Intro', 'Fill Details'];
            const isActive = step === item;
            const isDone = step === 'questions' && item === 'intro';

            return (
              <div key={item} className='flex items-center'>
                <div className='flex items-center gap-1.5'>
                  <div
                    className={cn(
                      'w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold',
                      isActive
                        ? 'bg-blue-600 text-white'
                        : isDone
                          ? 'bg-green-700 text-white'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-500',
                    )}
                  >
                    {isDone ? '✓' : index + 1}
                  </div>
                  <span
                    className={cn(
                      'text-xs font-medium',
                      isActive
                        ? 'text-blue-600 dark:text-blue-400'
                        : isDone
                          ? 'text-green-600'
                          : 'text-gray-400',
                    )}
                  >
                    {labels[index]}
                  </span>
                </div>
                {index < 1 && (
                  <div className='w-6 h-px bg-gray-200 dark:bg-gray-700 mx-2' />
                )}
              </div>
            );
          })}
        </div>

        <div className='flex-1 overflow-y-auto'>
          {step === 'intro' && (
            <div className='space-y-5 p-5'>
              <div className='rounded-xl border border-gray-200 dark:border-gray-800 bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-gray-900 p-5'>
                <h3 className='font-semibold text-gray-900 dark:text-white mb-2'>
                  What this Guru needs
                </h3>
                <p className='text-sm text-gray-600 dark:text-gray-300 leading-relaxed'>
                  Enter the brand and product context from your intake sheet so
                  the Social Listening Tool can analyze conversation themes,
                  sentiment, audience language, and market signals with the
                  right category framing.
                </p>
              </div>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                <div className='rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4'>
                  <h4 className='font-semibold text-gray-900 dark:text-white mb-2 text-sm'>
                    Brand details
                  </h4>
                  <ul className='space-y-1.5 text-sm text-gray-600 dark:text-gray-300'>
                    {[
                      'Brand name and website',
                      'Category, SOI, and tagline',
                      'HQ, region, launch year, and price point',
                    ].map((item) => (
                      <li key={item} className='flex items-start gap-2'>
                        <div className='mt-1.5 size-1.5 rounded-full bg-blue-600 flex-shrink-0' />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className='rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4'>
                  <h4 className='font-semibold text-gray-900 dark:text-white mb-2 text-sm'>
                    Product details
                  </h4>
                  <ul className='space-y-1.5 text-sm text-gray-600 dark:text-gray-300'>
                    {[
                      'Key product lines and products',
                      'Excited and concerned product focus',
                      'Distribution channels and key customers',
                    ].map((item) => (
                      <li key={item} className='flex items-start gap-2'>
                        <div className='mt-1.5 size-1.5 rounded-full bg-green-600 flex-shrink-0' />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {step === 'questions' && (
            <div className='p-5 space-y-4'>
              {fields.map((field) => (
                <div
                  key={field.id}
                  className='grid grid-cols-1 sm:grid-cols-[190px_1fr] gap-2 sm:gap-3 sm:items-start'
                >
                  <label className='text-sm font-medium text-gray-600 dark:text-gray-400 sm:pt-2'>
                    {field.label}
                  </label>
                  {field.multiline ? (
                    <Textarea
                      value={form[field.id]}
                      onChange={(event) =>
                        handleFieldChange(field.id, event.target.value)
                      }
                      placeholder={field.placeholder}
                      className={`min-h-20 resize-y ${inputCls}`}
                    />
                  ) : (
                    <Input
                      type='text'
                      value={form[field.id]}
                      onChange={(event) =>
                        handleFieldChange(field.id, event.target.value)
                      }
                      inputMode={
                        field.id === 'launchYear' ? 'numeric' : undefined
                      }
                      pattern={field.id === 'launchYear' ? '\\d*' : undefined}
                      placeholder={field.placeholder}
                      className={`h-9 ${inputCls}`}
                    />
                  )}
                </div>
              ))}
              <div className='rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4'>
                <p className='text-sm text-amber-800 dark:text-amber-200'>
                  These fields are optional. You can also add more details in
                  chat before starting the analysis.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className='flex items-center justify-between gap-4 p-5 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50'>
          {step === 'intro' ? (
            <>
              <Button
                type='button'
                variant='outline'
                onClick={handleClose}
                className='dark:!text-white'
              >
                Cancel
              </Button>
              <Button
                type='button'
                onClick={() => setStep('questions')}
                className='px-8'
              >
                Next
              </Button>
            </>
          ) : (
            <>
              <Button
                type='button'
                variant='outline'
                onClick={() => setStep('intro')}
                className='dark:!text-white'
              >
                Back
              </Button>
              <Button type='button' onClick={handleStart} className='px-8'>
                Start Analysis
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
